(function () {
  'use strict';

  // Bind cart page behavior once (survives section morphs via document delegation).
  if (window.__bb_cart_bound) return;
  window.__bb_cart_bound = true;

  // Shared helpers from bb-core.js (must load first).
  const BB = window.BB;
  if (!BB) return;

  const shop_root = BB.shop_root;
  const cart_page = BB.cart_page;
  const section_ids = BB.section_ids;
  const sections_payload = BB.sections_payload;
  const post_cart = BB.post_cart;
  const fetch_cart = BB.fetch_cart;
  const fetch_sections = BB.fetch_sections;
  const refresh_after_cart_change = function (result) {
    return BB.refresh_after_cart_change(result, { source: 'bb-cart' });
  };
  const set_loading = BB.set_cart_loading;
  const parse_json = BB.parse_json;

  // ---------------------------------------------------------------------------
  // DOM / cart helpers
  // ---------------------------------------------------------------------------

  // Return the cart products block root (holds money format + line items).
  function products_root() {
    return document.querySelector('[data-bb-cart-products]');
  }

  // Find a cart row by its stable display index (survives section morphs).
  function row_for_line_index(line_index) {
    return document.querySelector('[data-bb-cart-row][data-line-index="' + line_index + '"]');
  }

  // Resolve the live config panel for a row after section morphs replace line keys.
  function config_for_line_index(line_index) {
    const row = row_for_line_index(line_index);
    return row ? row.querySelector('[data-bb-cart-config]') : null;
  }

  // Find the config panel for a clicked/changed control.
  function get_config(target) {
    return target.closest('[data-bb-cart-config]');
  }

  // Return the cart discount block root from an event target.
  function discount_root(target) {
    return target.closest('[data-bb-cart-discount]');
  }

  // Format cents using the shop money format, with a simple fallback.
  function format_money(cents) {
    const root = products_root();
    const money_format = root ? root.getAttribute('data-money-format') || '' : '';
    return BB.format_money(cents, money_format);
  }

  // Read applied discount codes from a cart.js / update.js response.
  function discount_codes_from_cart(cart) {
    return (cart && cart.discount_codes ? cart.discount_codes : [])
      .filter(function (entry) {
        // Keep codes Shopify still considers applicable.
        return entry && entry.code && entry.applicable !== false;
      })
      .map(function (entry) {
        return entry.code;
      });
  }

  // Join discount codes into the Cart Ajax API discount string.
  function discount_codes_string(codes) {
    return (codes || []).filter(Boolean).join(',');
  }

  // Case-insensitive check for an existing discount code.
  function has_discount_code(codes, code) {
    const needle = String(code || '').toLowerCase();

    return (codes || []).some(function (value) {
      return String(value).toLowerCase() === needle;
    });
  }

  // Snapshot cart note/attributes/discounts to restore after a variant swap.
  function cart_meta_from(cart) {
    return {
      note: cart.note || '',
      attributes: cart.attributes || {},
      discount: discount_codes_string(discount_codes_from_cart(cart)),
    };
  }

  // Refresh section HTML after a mutation, or no-op when the chain short-circuits.
  function refresh_cart(result) {
    if (!result) return null;
    return refresh_after_cart_change(result);
  }

  // Show an update error inside the editing panel for a line index.
  function show_config_error(line_index, message) {
    const config = config_for_line_index(line_index);
    const note = config && config.querySelector('[data-bb-cart-config-note]');
    // Exit when the morphed config panel is no longer on the page.
    if (!note) return;
    note.textContent = message;
    note.classList.add('is-error');
  }

  // Serialize cart page mutations so Ajax flows never overlap.
  let cart_tail = Promise.resolve();

  function run_cart(fn) {
    const job = cart_tail.then(fn);
    cart_tail = job.catch(function () {}).then(function () {});
    return job;
  }

  // Recs on the cart page shares this queue when bb-cart.js is loaded.
  BB.run_cart = run_cart;

  // ---------------------------------------------------------------------------
  // Cart API payloads
  // ---------------------------------------------------------------------------

  // Build the replacement line for a variant swap.
  function build_replacement_line(source_item, variant_id, quantity, selling_plan) {
    const properties = Object.assign({}, source_item.properties || {});
    delete properties._bb_cart_pos;

    const line = {
      id: Number(variant_id),
      quantity: Number(quantity || 1),
      properties: properties,
    };

    if (selling_plan) line.selling_plan = Number(selling_plan);

    return line;
  }

  // Add one or more items to the cart in a single request.
  function add_cart_items(items, payload) {
    return post_cart(shop_root() + 'cart/add.js', Object.assign({ items: items }, payload || {}));
  }

  // Restore note/attributes/discounts and return section HTML when possible.
  function finalize_cart_response(cart_meta, ids, add_result) {
    const root = shop_root();
    const payload = sections_payload(ids);
    const update_payload = Object.assign({}, payload);

    if (cart_meta.note) update_payload.note = cart_meta.note;
    if (cart_meta.attributes && Object.keys(cart_meta.attributes).length) {
      update_payload.attributes = cart_meta.attributes;
    }
    if (cart_meta.discount) update_payload.discount = cart_meta.discount;

    const has_meta = Boolean(update_payload.note || update_payload.attributes || update_payload.discount);

    // Prefer update.js when meta must be restored (also requests sections).
    if (has_meta) return post_cart(root + 'cart/update.js', update_payload);
    // Use sections already returned by add.js when present.
    if (add_result && add_result.sections && Object.keys(add_result.sections).length) return add_result;
    // No sections requested — nothing more to do.
    if (!ids.length) return Promise.resolve({ sections: {} });

    return fetch_sections(ids).then(function (sections) {
      return { sections: sections };
    });
  }

  // Remove the old line and add the new variant (Shopify cart order).
  function swap_line_variant(line_key, line, cart_meta, ids, payload) {
    const root = shop_root();

    return post_cart(root + 'cart/change.js', Object.assign({ id: line_key, quantity: 0 }, payload))
      .then(function () {
        return add_cart_items([line], payload);
      })
      .then(function (add_result) {
        return finalize_cart_response(cart_meta, ids, add_result);
      });
  }

  // Build the remove request for a standard line or a bundle row.
  function remove_request_for_row(row, payload) {
    const line_key = row.getAttribute('data-key');
    const bundle_keys = (row.getAttribute('data-bundle-keys') || '')
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);

    // Bundles: zero out every related line key in one update call.
    if (bundle_keys.length) {
      const updates = {};
      bundle_keys.forEach(function (bundle_key) {
        updates[bundle_key] = 0;
      });

      return post_cart(shop_root() + 'cart/update.js', Object.assign({ updates: updates }, payload));
    }

    // Exit when the row has no removable line key.
    if (!line_key) return null;

    return post_cart(shop_root() + 'cart/change.js', Object.assign({ id: line_key, quantity: 0 }, payload));
  }

  // ---------------------------------------------------------------------------
  // Config editing
  // ---------------------------------------------------------------------------

  // Read embedded product variants JSON from the config panel.
  function get_variants(config) {
    return parse_json(config.querySelector('[data-bb-cart-config-variants]')) || [];
  }

  // Read currently selected option values in option position order.
  function get_selected_options(config) {
    return Array.from(config.querySelectorAll('[data-bb-cart-config-option]'))
      .sort(function (option_a, option_b) {
        return Number(option_a.dataset.optionPosition) - Number(option_b.dataset.optionPosition);
      })
      .map(function (select) {
        return select.value;
      });
  }

  // Match a variant by selected option1/option2/option3 values.
  function find_variant(variants, options) {
    return variants.find(function (variant) {
      return options.every(function (value, index) {
        return String(variant['option' + (index + 1)] || '') === String(value || '');
      });
    });
  }

  // Apply the line's promo percent to a variant price (cents).
  function priced_cents(config, variant_price) {
    const promo_percent = Number(config.dataset.promoPercent || 0);
    const price = Number(variant_price || 0);

    // No promo adjustment when percent is zero/missing.
    if (promo_percent <= 0) return price;

    return Math.round(price - (price * promo_percent) / 100);
  }

  // Toggle editing mode on a config panel and refresh the preview when entering.
  function set_editing(config, editing) {
    config.classList.toggle('is-editing', editing);
    // Sync price preview as soon as editing starts.
    if (editing) update_preview(config);
  }

  // Restore selects to the line's current cart variant.
  function reset_selects(config) {
    const variants = get_variants(config);
    const current_variant = variants.find(function (variant) {
      return String(variant.id) === String(config.dataset.variantId);
    });

    // Exit when the current variant cannot be found in the embedded JSON.
    if (!current_variant) return;

    config.querySelectorAll('[data-bb-cart-config-option]').forEach(function (select) {
      const position = Number(select.dataset.optionPosition);
      const value = current_variant['option' + position];
      // Skip options the variant does not define.
      if (value == null) return;
      select.value = value;
    });
  }

  // Update the editing footer price + note based on the selected options.
  function update_preview(config) {
    const price_element = config.querySelector('[data-bb-cart-config-price]');
    const note_element = config.querySelector('[data-bb-cart-config-note]');
    const update_button = config.querySelector('[data-bb-cart-config-update]');
    const variant = find_variant(get_variants(config), get_selected_options(config));
    const current_price = Number(config.dataset.currentPrice || 0);

    // No matching variant for this option combination.
    if (!variant) {
      if (price_element) price_element.textContent = format_money(current_price);
      if (note_element) {
        note_element.textContent = 'Unavailable combination';
        note_element.classList.add('is-error');
      }
      if (update_button) update_button.disabled = true;
      return;
    }

    // Variant exists but is out of stock.
    if (!variant.available) {
      if (price_element) price_element.textContent = format_money(priced_cents(config, variant.price));
      if (note_element) {
        note_element.textContent = 'Out of stock';
        note_element.classList.add('is-error');
      }
      if (update_button) update_button.disabled = true;
      return;
    }

    const preview_price = priced_cents(config, variant.price);
    const price_difference = preview_price - current_price;

    if (price_element) price_element.textContent = format_money(preview_price);
    if (note_element) note_element.classList.remove('is-error');
    if (update_button) update_button.disabled = false;
    // Exit when there is nowhere to write the note text.
    if (!note_element) return;

    // Same price as the current cart line.
    if (price_difference === 0) {
      note_element.textContent = 'No change to your price';
      return;
    }

    const signed_difference =
      price_difference > 0
        ? '(+' + format_money(price_difference)
        : '(-' + format_money(Math.abs(price_difference));
    note_element.textContent = signed_difference + ' vs. current selection)';
  }

  // Run the queued variant swap against the live DOM for one line index.
  function commit_line_variant_update(line_index) {
    const live_config = config_for_line_index(line_index);
    // The line may have been removed by another tab/action.
    if (!live_config) throw new Error('Cart line item not found');

    const live_variant = find_variant(get_variants(live_config), get_selected_options(live_config));
    const line_key = live_config.dataset.key;
    const quantity = Number(live_config.dataset.quantity || 1);
    const ids = section_ids();
    const payload = sections_payload(ids);
    const update_button = live_config.querySelector('[data-bb-cart-config-update]');

    // Exit when the selection is invalid or the line key is missing.
    if (!live_variant || !live_variant.available || !line_key) return null;

    // Same variant selected — just leave editing mode.
    if (String(live_variant.id) === String(live_config.dataset.variantId)) {
      set_editing(live_config, false);
      return null;
    }

    if (update_button) update_button.disabled = true;
    set_loading(true);

    return fetch_cart()
      .then(function (cart) {
        const source_item = (cart.items || []).find(function (item) {
          return item.key === line_key;
        });

        if (!source_item) throw new Error('Cart line item not found');

        const selling_plan =
          live_config.dataset.sellingPlan ||
          (source_item.selling_plan_allocation && source_item.selling_plan_allocation.selling_plan
            ? source_item.selling_plan_allocation.selling_plan.id
            : null);

        return swap_line_variant(
          line_key,
          build_replacement_line(source_item, live_variant.id, quantity, selling_plan),
          cart_meta_from(cart),
          ids,
          payload
        );
      })
      .then(refresh_cart)
      .catch(function (error) {
        console.error(error);
        show_config_error(line_index, error.message || 'Unable to update item');
      })
      .finally(function () {
        const done_config = config_for_line_index(line_index);
        const done_button = done_config && done_config.querySelector('[data-bb-cart-config-update]');
        if (done_button) done_button.disabled = false;
        set_loading(false);
      });
  }

  // Swap the edited line to a new variant (change + add).
  function update_line_variant(config) {
    const row = config.closest('[data-bb-cart-row]');
    const line_index = row ? row.getAttribute('data-line-index') : '';
    // Exit when the row has no stable index to resolve after morphs.
    if (!line_index) return;

    const variant = find_variant(get_variants(config), get_selected_options(config));
    if (!variant || !variant.available) return;

    // Same variant selected — just leave editing mode.
    if (String(variant.id) === String(config.dataset.variantId)) {
      set_editing(config, false);
      return;
    }

    return run_cart(function () {
      return commit_line_variant_update(line_index);
    });
  }

  // ---------------------------------------------------------------------------
  // Remove / discount
  // ---------------------------------------------------------------------------

  // Remove a single line item, or every line in a bundle.
  function remove_line(row) {
    if (!row) return;

    const line_index = row.getAttribute('data-line-index');
    if (!line_index) return;

    return run_cart(function () {
      const live_row = row_for_line_index(line_index);
      // Exit when the row was removed before this queued job ran.
      if (!live_row) return null;

      const payload = sections_payload(section_ids());
      const request = remove_request_for_row(live_row, payload);
      // Nothing to send when we could not resolve a key.
      if (!request) return null;

      set_loading(true);

      return request
        .then(refresh_cart)
        .catch(function (error) {
          console.error(error);
        })
        .finally(function () {
          set_loading(false);
        });
    });
  }

  // Show or clear the discount apply error message.
  function set_discount_error(root, message) {
    const error = root.querySelector('[data-bb-cart-discount-error]');

    root.classList.toggle('is-error', Boolean(message));
    if (error) error.textContent = message || '';
  }

  // POST discount codes through the shared Cart Ajax update path.
  function post_discount_update(codes) {
    return post_cart(
      shop_root() + 'cart/update.js',
      Object.assign({ discount: discount_codes_string(codes) }, sections_payload())
    );
  }

  // Apply a discount code via Cart Ajax API, then refresh cart sections.
  function apply_discount(root) {
    const input = root.querySelector('[data-bb-cart-discount-input]');
    const apply_button = root.querySelector('[data-bb-cart-discount-apply]');
    const code = input ? String(input.value || '').trim() : '';

    // Ignore empty submissions.
    if (!code) {
      set_discount_error(root, 'Enter a discount code');
      if (input) input.focus();
      return;
    }

    set_discount_error(root, '');
    if (apply_button) apply_button.disabled = true;

    return run_cart(function () {
      set_loading(true);

      return fetch_cart()
        .then(function (cart) {
          const existing = discount_codes_from_cart(cart);

          // Skip when the code is already applied.
          if (has_discount_code(existing, code)) {
            if (input) input.value = '';
            return null;
          }

          return post_discount_update(existing.concat([code]));
        })
        .then(function (result) {
          // Nothing to refresh when the code was already present.
          if (!result) return null;

          const matched = (result.discount_codes || []).find(function (entry) {
            return entry && String(entry.code).toLowerCase() === code.toLowerCase();
          });

          // Only fail when Shopify explicitly rejects the code.
          if (!matched || matched.applicable === false) {
            if (input) input.value = '';
            set_discount_error(root, 'Discount code cannot be applied to your cart');
            return null;
          }

          if (input) input.value = '';
          return refresh_cart(result);
        })
        .catch(function (error) {
          set_discount_error(root, error.message || 'Unable to apply discount code');
        })
        .finally(function () {
          if (apply_button) apply_button.disabled = false;
          set_loading(false);
        });
    });
  }

  // Remove one applied discount code and refresh cart sections.
  function remove_discount(root, code) {
    set_discount_error(root, '');

    return run_cart(function () {
      set_loading(true);

      return fetch_cart()
        .then(function (cart) {
          const existing = discount_codes_from_cart(cart);
          const next = existing.filter(function (value) {
            return String(value).toLowerCase() !== String(code || '').toLowerCase();
          });

          // Exit when the code is not in the applied list.
          if (next.length === existing.length) return null;

          return post_discount_update(next);
        })
        .then(refresh_cart)
        .catch(function (error) {
          set_discount_error(root, error.message || 'Unable to remove discount code');
        })
        .finally(function () {
          set_loading(false);
        });
    });
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  // Handle remove + discount + config edit/cancel/update clicks on the cart page.
  document.addEventListener('click', function (event) {
    const page = cart_page();
    // Ignore clicks when the cart page root is not present.
    if (!page) return;

    const remove_button = event.target.closest('[data-bb-cart-remove]');
    // Remove button inside a cart row.
    if (remove_button && page.contains(remove_button)) {
      remove_line(remove_button.closest('[data-bb-cart-row]'));
      return;
    }

    const discount_remove = event.target.closest('[data-bb-cart-discount-remove]');
    // Remove an applied discount pill.
    if (discount_remove && page.contains(discount_remove)) {
      const remove_root = discount_root(discount_remove);
      const pill = discount_remove.closest('[data-discount-code]');
      if (remove_root && pill) remove_discount(remove_root, pill.getAttribute('data-discount-code'));
      return;
    }

    const products = products_root();
    // Config actions only apply when the products block is mounted.
    if (!products) return;

    const edit_button = event.target.closest('[data-bb-cart-config-edit]');
    // Enter editing mode for this line's config panel.
    if (edit_button && products.contains(edit_button)) {
      const edit_config = get_config(edit_button);
      if (edit_config) set_editing(edit_config, true);
      return;
    }

    const cancel_button = event.target.closest('[data-bb-cart-config-cancel]');
    // Cancel editing and restore the original option values.
    if (cancel_button && products.contains(cancel_button)) {
      const cancel_config = get_config(cancel_button);
      // Exit when the cancel control is outside a config panel.
      if (!cancel_config) return;
      reset_selects(cancel_config);
      set_editing(cancel_config, false);
      return;
    }

    const update_button = event.target.closest('[data-bb-cart-config-update]');
    // Ignore clicks that are not an Update item control inside products.
    if (!update_button || !products.contains(update_button)) return;

    const update_config = get_config(update_button);
    if (update_config) update_line_variant(update_config);
  });

  // Apply discount codes from the summary form.
  document.addEventListener('submit', function (event) {
    const page = cart_page();
    // Ignore submits when the cart page root is not present.
    if (!page) return;

    const form = event.target.closest('[data-bb-cart-discount-form]');
    // Ignore submits outside the cart discount form.
    if (!form || !page.contains(form)) return;

    event.preventDefault();

    const root = discount_root(form);
    if (root) apply_discount(root);
  });

  // Keep the editing price preview in sync when option selects change.
  document.addEventListener('change', function (event) {
    const products = products_root();
    // Ignore changes when the products block is not mounted.
    if (!products) return;

    const select = event.target.closest('[data-bb-cart-config-option]');
    // Ignore changes outside config option selects.
    if (!select || !products.contains(select)) return;

    const config = get_config(select);
    if (config) update_preview(config);
  });
})();
